import {
	ErrorComponent,
	type ErrorComponentProps,
	useRouter,
} from "@tanstack/react-router";
import { Button, Typography } from "@mui/material";
import { t } from "i18next";
import { NotFoundErrorPage } from "./fallbacks/not-found-error-page/not-found-error-page";
import { UnknownErrorPage } from "./fallbacks/unknown-error-page/unknown-error-page";
import { Flex } from "@/components/layout/flex/flex";

export function RouteErrorComponent({ error }: ErrorComponentProps) {
	const router = useRouter();

	// TODO(monitoring): report non-404 route errors to the new error-monitoring
	// system once it's in place.

	// Check if it's a 404 error
	if (error.message.includes("404") || error.message.includes("Not Found")) {
		return <NotFoundErrorPage />;
	}

	function handleGoHome() {
		void router.navigate({ to: "/" });
	}

	// Custom actions for route errors
	const actions = (
		<Flex mt={2} gap={2} justifyContent="center">
			<Button onClick={handleGoHome}>{t("Back to Home")}</Button>
		</Flex>
	);

	// For development, also show the default error component for debugging
	const isDevelopment = import.meta.env.DEV;

	return (
		<>
			<UnknownErrorPage actions={actions} />
			{isDevelopment && (
				<Flex mt={4} p={2} bgcolor="background.paper" borderRadius={1}>
					<Typography
						variant="caption"
						component="pre"
						sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
					>
						{error.stack || error.message}
					</Typography>
				</Flex>
			)}
		</>
	);
}

// Export a fallback to the default ErrorComponent for unhandled cases
export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
	// Check if we should use our custom component
	if (error instanceof Error) {
		return <RouteErrorComponent error={error} reset={reset} />;
	}

	// Fallback to TanStack's default error component
	return <ErrorComponent error={error} />;
}
